import React from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency, DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { getLocalizedValue } from '../../constants/Languages';
import { BRAND } from '../../theme/brandTokens';

const formatWeight = (weight) => {
  const value = Number(weight ?? 0);
  if (!Number.isFinite(value)) {
    return '0';
  }
  return value.toFixed(2).replace(/\.?0+$/, '') || '0';
};

const MembershipPeriodUsageBreakdownTable = ({
  periods = [],
  currencyCode = DEFAULT_CURRENCY,
}) => {
  const { t, i18n } = useTranslation();

  if (!periods.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('employeePayments.payroll.membershipPeriod.empty')}
      </Typography>
    );
  }

  return (
    <Stack spacing={2.5}>
      {periods.map((period) => (
        <Box
          key={`${period.customerMembershipId}-${period.billingPeriodStartUtc}`}
          sx={{ border: `1px solid ${BRAND.line}`, borderRadius: 2, overflow: 'hidden' }}
        >
          <Box sx={{ px: 2, py: 1.5, backgroundColor: BRAND.background }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
              {period.customerDisplayName}
              {getLocalizedValue(period.planName, i18n.language) ? ` · ${getLocalizedValue(period.planName, i18n.language)}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('employeePayments.payroll.membershipPeriod.periodDates', {
                from: formatUtcDate(period.billingPeriodStartUtc),
                to: formatUtcDate(period.billingPeriodEndUtc),
              })}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('employeePayments.payroll.membershipPeriod.member')}</TableCell>
                  <TableCell align="right">{t('employeePayments.payroll.membershipPeriod.redemptions')}</TableCell>
                  <TableCell align="right">{t('employeePayments.payroll.membershipPeriod.shareAmount')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(period.memberShares || []).map((share) => (
                  <TableRow key={share.teamMemberId}>
                    <TableCell>{share.teamMemberName}</TableCell>
                    <TableCell align="right">{formatWeight(share.redemptionWeight)}</TableCell>
                    <TableCell align="right">{formatCurrency(share.shareAmount, currencyCode)}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2}>
                    {t('employeePayments.payroll.membershipPeriod.keptByStore')}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(period.unallocatedAmount, currencyCode)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Stack>
  );
};

export default MembershipPeriodUsageBreakdownTable;
